import React, { useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Chip,
  useTheme,
  alpha,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  LocalShipping,
  PhotoCamera,
  Schedule,
} from '@mui/icons-material'
import { ShipmentPlan } from '@otw/models/logistics'
import { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'

export interface DraftTruckItem {
  truckWithVehicle: TransportTruckWithVehicle
}

export interface PhotoApprovalItem {
  truckWithVehicle: TransportTruckWithVehicle
  plan: ShipmentPlan
  legIndex: number
}

export interface MissingReadyDateItem {
  truckWithVehicle: TransportTruckWithVehicle
  plan: ShipmentPlan
  legIndex: number
}

interface DashboardCardV2Props {
  transportTrucksWithVehicles: TransportTruckWithVehicle[]
  onDraftTruckClick?: (item: DraftTruckItem) => void
  onPhotoApprovalClick?: (item: PhotoApprovalItem) => void
  onMissingReadyDateClick?: (item: MissingReadyDateItem) => void
}

type ActionId = 'draft' | 'photos' | 'readyDate'

interface DashboardAction {
  id: ActionId
  title: string
  description: string
  count: number
  color: string
  icon: React.ReactNode
}

function getVehicleLabel(item: TransportTruckWithVehicle): string {
  const yearMakeModel = item.vehicle
    ? `${item.vehicle.year ?? ''} ${item.vehicle.make ?? ''} ${item.vehicle.model ?? ''}`.trim()
    : 'Unknown vehicle'
  const vinLast4 = item.truck.vin?.slice(-4) ?? '----'
  return `${yearMakeModel} (${vinLast4})`
}

function getLocationContactLabel(
  locationId: string,
  locationType: 'pickup' | 'repair',
  pickupLocations: Array<{ id?: string; contact_name?: string }> | undefined,
  repairShopLocations:
    | Array<{ id?: string; name?: string; contact_name?: string }>
    | undefined
): string {
  if (locationType === 'repair') {
    const shop = repairShopLocations?.find((r) => r.id === locationId)
    return shop?.name || shop?.contact_name || 'Unknown shop'
  }
  const pickup = pickupLocations?.find((p) => p.id === locationId)
  return pickup?.contact_name || 'Unknown contact'
}

function renderActionListItem(
  label: string,
  color: string,
  onClick?: () => void
) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: 0.75,
        backgroundColor: alpha(color, 0.03),
        border: `1px solid ${alpha(color, 0.1)}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              backgroundColor: alpha(color, 0.08),
              borderColor: alpha(color, 0.2),
            }
          : undefined,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={label}
      >
        {label}
      </Typography>
      <Chip
        label="View"
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 500,
          fontSize: '0.6rem',
          height: '20px',
        }}
      />
    </Box>
  )
}

const DashboardCardV2: React.FC<DashboardCardV2Props> = ({
  transportTrucksWithVehicles,
  onDraftTruckClick,
  onPhotoApprovalClick,
  onMissingReadyDateClick,
}) => {
  const theme = useTheme()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const [expandedActions, setExpandedActions] = useState<Set<ActionId>>(new Set())

  const { draftTrucks, photosNeedingApproval, legsWithoutReadyDate } = useMemo(() => {
    const draft: DraftTruckItem[] = []
    const approval: PhotoApprovalItem[] = []
    const missingReadyDate: MissingReadyDateItem[] = []

    for (const truckWithVehicle of transportTrucksWithVehicles) {
      const latestTruckStatus = truckWithVehicle.truck.status_history?.length
        ? truckWithVehicle.truck.status_history[truckWithVehicle.truck.status_history.length - 1].status
        : 'draft'
      if (latestTruckStatus === 'draft') {
        draft.push({ truckWithVehicle })
      }

      for (const plan of truckWithVehicle.shipmentPlans ?? []) {
        const legs = plan.shipment_legs ?? []
        legs.forEach((leg, legIndex) => {
          const latestNote = leg.status_history?.length
            ? leg.status_history[leg.status_history.length - 1]?.notes
            : undefined

          const needsPreDeliveryApproval =
            latestNote === 'Pre-delivery photos completed' &&
            leg.to_type !== 'repair'
          const needsPostRepairApproval =
            latestNote === 'Post-repair photos completed'

          if (needsPreDeliveryApproval || needsPostRepairApproval) {
            approval.push({ truckWithVehicle, plan, legIndex })
          }

          if (!leg.ready_date || leg.ready_date.trim() === '') {
            missingReadyDate.push({ truckWithVehicle, plan, legIndex })
          }
        })
      }
    }

    return {
      draftTrucks: draft,
      photosNeedingApproval: approval,
      legsWithoutReadyDate: missingReadyDate,
    }
  }, [transportTrucksWithVehicles])

  const actions: DashboardAction[] = useMemo(
    () => [
      {
        id: 'draft',
        title: 'Draft Trucks',
        description: 'Transport trucks that are still in draft status.',
        count: draftTrucks.length,
        color: theme.palette.info.main,
        icon: <LocalShipping />,
      },
      {
        id: 'photos',
        title: 'Photos Needing Approval',
        description: 'Legs where completed photos still need admin approval.',
        count: photosNeedingApproval.length,
        color: theme.palette.success.main,
        icon: <PhotoCamera />,
      },
      {
        id: 'readyDate',
        title: 'Legs Missing Ready Date',
        description: 'Legs that do not have a ready date set.',
        count: legsWithoutReadyDate.length,
        color: theme.palette.warning.main,
        icon: <Schedule />,
      },
    ],
    [draftTrucks.length, photosNeedingApproval.length, legsWithoutReadyDate.length, theme]
  )

  const toggleAction = (actionId: ActionId) => {
    setExpandedActions((prev) => {
      const next = new Set(prev)
      if (next.has(actionId)) {
        next.delete(actionId)
      } else {
        next.add(actionId)
      }
      return next
    })
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 0,
        borderColor: alpha(theme.palette.primary.main, 0.2),
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box
          sx={{
            p: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          {actions.map((action) => {
            const isActionExpanded = expandedActions.has(action.id)
            const hasItems = action.count > 0

            return (
              <Box key={action.id} sx={{ height: '100%', display: 'flex' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: alpha(action.color, 0.25),
                    borderRadius: 1.5,
                    bgcolor: alpha(action.color, 0.04),
                  }}
                >
                  <Box
                    onClick={() => hasItems && toggleAction(action.id)}
                    sx={{
                      p: 1.5,
                      minHeight: 84,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      cursor: hasItems ? 'pointer' : 'default',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(action.color, 0.14),
                          color: action.color,
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {action.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={action.count}
                        size="small"
                        sx={{
                          bgcolor: action.count > 0 ? alpha(action.color, 0.18) : undefined,
                          color: action.count > 0 ? action.color : undefined,
                          fontWeight: 600,
                        }}
                      />
                      {hasItems && (
                        <IconButton size="small">
                          {isActionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {action.id === 'draft' && (
                    <Collapse in={isActionExpanded}>
                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                        {draftTrucks.map((item) => (
                          <Box key={item.truckWithVehicle.truck.id} sx={{ mb: 1 }}>
                            {renderActionListItem(
                              getVehicleLabel(item.truckWithVehicle),
                              theme.palette.info.main,
                              onDraftTruckClick ? () => onDraftTruckClick(item) : undefined
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}

                  {action.id === 'photos' && (
                    <Collapse in={isActionExpanded}>
                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                        {photosNeedingApproval.map((item) => (
                          <Box key={`${item.plan.id}-${item.legIndex}`} sx={{ mb: 1 }}>
                            {renderActionListItem(
                              `${getVehicleLabel(item.truckWithVehicle)} - ${getLocationContactLabel(
                                item.plan.shipment_legs?.[item.legIndex]?.from_id || '',
                                item.plan.shipment_legs?.[item.legIndex]?.from_type || 'pickup',
                                pickupLocations,
                                repairShopLocations
                              )} -> ${getLocationContactLabel(
                                item.plan.shipment_legs?.[item.legIndex]?.to_id || '',
                                item.plan.shipment_legs?.[item.legIndex]?.to_type || 'pickup',
                                pickupLocations,
                                repairShopLocations
                              )}`,
                              theme.palette.success.main,
                              onPhotoApprovalClick ? () => onPhotoApprovalClick(item) : undefined
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}

                  {action.id === 'readyDate' && (
                    <Collapse in={isActionExpanded}>
                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                        {legsWithoutReadyDate.map((item) => (
                          <Box key={`${item.plan.id}-${item.legIndex}`} sx={{ mb: 1 }}>
                            {renderActionListItem(
                              `${getVehicleLabel(item.truckWithVehicle)} - Leg ${item.legIndex + 1}`,
                              theme.palette.warning.main,
                              onMissingReadyDateClick ? () => onMissingReadyDateClick(item) : undefined
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}

export default DashboardCardV2
